"use client";
import React, { useMemo, useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { motion } from 'motion/react';
import Loading from '@/components/app/loading/LoadingSpinner';
import { LeaveReviewDataProvider, useLeaveReviewData } from '@/components/context/LeaveReviewDataProvider';
import { LeaveReviewNavigationProvider, useLeaveReviewNavigation } from '@/components/context/LeaveReviewNavigationProvider';
import { LeaveReviewSessionProvider, useLeaveReviewSession } from '@/components/context/LeaveReviewSessionProvider';
import LeaveReviewErrorBoundary from '@/components/leave-review/ErrorBoundary';
import { 
  LEAVE_REVIEW_STEPS, 
  getCurrentStepFromPath, 
  getNavigationProgress,
  generateBreadcrumbs
} from '@/lib/navigation/leave-review-navigation';

interface AuthState {
  isAuthenticated: boolean;
  userType: 'tenant' | 'landlord' | 'admin' | null;
  userId?: string;
  email?: string;
}

interface ClientSideLayoutProps {
  children: React.ReactNode;
  initialAuth: AuthState;
}

// Optimized context data check with graceful degradation
function usePageData() {
  const pathname = usePathname();
  const isLandingPage = pathname === '/leave-review';
  
  // Always call hooks unconditionally (Rules of Hooks)
  const dataContext = useLeaveReviewData();
  const navigationContext = useLeaveReviewNavigation();
  
  // Landing page never needs context (it's outside providers)
  if (isLandingPage) {
    return { isReady: true, needsContext: false };
  }
  
  const hasDataContext = dataContext && typeof dataContext === 'object';
  const hasNavigationContext = navigationContext && typeof navigationContext === 'object';
  const hasFormData = hasDataContext && dataContext.formData && typeof dataContext.formData === 'object';
  
  // Consider ready if both contexts exist, even if formData is still initializing
  const isReady = hasDataContext && hasNavigationContext;
  
  return {
    isReady,
    needsContext: true,
    dataContext,
    navigationContext,
    hasFormData
  };
}

// Create a version of the component that doesn't use context
function ClientSideLayoutContentNoContext({ 
  children, 
  initialAuth 
}: { 
  children: React.ReactNode;
  initialAuth: AuthState;
}) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar initialAuth={initialAuth} />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Individual step component with navigation logic
function StepItem({ step, index, currentIdx, isNavigable, totalSteps, isCompleted, isValid }: {
  step: typeof LEAVE_REVIEW_STEPS[0];
  index: number;
  currentIdx: number;
  isNavigable: boolean;
  totalSteps: number;
  isCompleted: boolean;
  isValid: boolean;
}) {
  const [isHovering, setIsHovering] = useState(false);
  const isCurrent = index === currentIdx;
  
  const stepContent = (
    <div
      className="flex flex-col items-center flex-1 min-w-[50px] max-w-[70px] sm:min-w-0 sm:max-w-none mx-0.5 sm:mx-0"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.div
        className={`relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-300
          ${isCompleted ? 'bg-green-600 border-green-600 text-white shadow-sm' :
            isCurrent ? 'bg-blue-600 border-blue-600 text-white shadow-md ring-2 ring-blue-200' :
            'bg-gray-100 border-gray-300 text-gray-400'}
          ${isNavigable && isHovering ? 'scale-105 shadow-md' : ''}
          ${!isNavigable && index > currentIdx ? 'opacity-60' : ''}
        `}
        whileHover={isNavigable ? { scale: 1.03 } : {}}
        whileTap={isNavigable ? { scale: 0.97 } : {}}
      >
        <span className="flex items-center justify-center">
          {isCompleted ? (
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <step.icon size={14} className="sm:w-4 sm:h-4" />
          )}
        </span>
      </motion.div>
      <span 
        className={`mt-1 text-[10px] sm:text-xs font-medium text-center transition-colors duration-300 leading-tight truncate
          ${isCompleted ? 'text-green-600' :
            isCurrent ? 'text-blue-600' : 
            'text-gray-500'} 
          w-full max-w-[50px] sm:max-w-none mx-auto block`}
        title={step.label}
      >
        {step.label}
      </span>
      
      {/* Validation indicator */}
      {isCurrent && !isValid && (
        <div className="mt-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></div>
      )}
    </div>
  );
  
  // Only wrap with Link if navigation is allowed and it's a completed step
  if (isNavigable && index < currentIdx) {
    return (
      <Link
        href={step.path}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-md"
      >
        {stepContent}
      </Link>
    );
  }
  
  return stepContent;
}

function StepperWithContext() {
  const pathname = usePathname();
  
  const dataContext = useLeaveReviewData();
  const navigationContext = useLeaveReviewNavigation();
  
  const currentStep = useMemo(() => getCurrentStepFromPath(pathname), [pathname]);
  const currentIdx = useMemo(() => 
    LEAVE_REVIEW_STEPS.findIndex(step => step.id === currentStep?.id), 
    [currentStep?.id]
  );
  
  const progressWidth = useMemo(() => 
    currentStep ? getNavigationProgress(currentStep.id) : 0, 
    [currentStep]
  );
  
  const breadcrumbs = useMemo(() => 
    currentStep ? generateBreadcrumbs(currentStep.id) : [], 
    [currentStep]
  );

  const hasDataContext = dataContext && typeof dataContext === 'object';
  const hasNavigationContext = navigationContext && typeof navigationContext === 'object';
  
  if (!hasDataContext || !hasNavigationContext) {
    // Return loading skeleton instead of null for better UX
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  // Safely access context properties with fallbacks
  const formData = dataContext.formData;
  const isStepCompleted = navigationContext.isStepCompleted || (() => false);
  const validateStep = navigationContext.validateStep || (() => true);
  
  // Additional safety check for formData
  if (!formData) {
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center px-2 sm:px-0 pt-4 sm:pt-6 bg-white/80 backdrop-blur-sm border-b border-gray-100 shadow-sm z-20">
      <div className="relative w-full max-w-5xl flex flex-col items-center">
        <div className="hidden md:block w-full mb-3">
          <nav className="flex items-center space-x-1 text-xs text-gray-600">
            {breadcrumbs.map((breadcrumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span>/</span>}
                {breadcrumb.href ? (
                  <Link 
                    href={breadcrumb.href}
                    className="hover:text-blue-600 transition-colors"
                  >
                    {breadcrumb.label}
                  </Link>
                ) : (
                  <span className={breadcrumb.isActive ? "text-gray-900" : "text-blue-600 font-medium"}>
                    {breadcrumb.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
        
        <div className="absolute left-0 right-0 top-4 md:top-6 z-0 h-0.5 bg-gray-200 rounded-full overflow-hidden">
          <motion.div 
            className="h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressWidth}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
        
        <div className="flex w-full justify-between z-10 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] sm:overflow-visible px-1 sm:px-0">
          {LEAVE_REVIEW_STEPS.map((step, idx) => {
            const isCompleted = isStepCompleted(step.id);
            const isValid = validateStep(step.id, formData);
            const isNavigable = idx < currentIdx;
            
            return (
              <StepItem
                key={step.path}
                step={step}
                index={idx}
                currentIdx={currentIdx}
                isNavigable={isNavigable}
                totalSteps={LEAVE_REVIEW_STEPS.length}
                isCompleted={isCompleted}
                isValid={isValid}
              />
            );
          })}
        </div>
        
        <div className="mt-3 mb-2 text-center">
          <span className="text-xs sm:text-sm text-gray-700">
            Step {Math.max(currentIdx + 1, 1)} of {LEAVE_REVIEW_STEPS.length}
          </span>
          {currentStep && (
            <span className="block text-[10px] sm:text-xs text-gray-500 mt-0.5">
              {currentStep.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientSideLayoutContent({ 
  children, 
  initialAuth 
}: { 
  children: React.ReactNode;
  initialAuth: AuthState;
}) {
  const pathname = usePathname();
  const { isReady } = usePageData();
  
  const isLandingPage = pathname === '/leave-review';
  const isPreparationPage = pathname === '/leave-review/preparation';

  if (!isReady) {
    return <Loading />;
  }

  // Simplified stepper visibility: show unless it's landing page or landlord/unauth on preparation
  const hideStepperConditions = isLandingPage || 
    (isPreparationPage && (initialAuth.userType === 'landlord' || !initialAuth.isAuthenticated));
  
  const showStepper = !hideStepperConditions;

  return (
    <div className="flex flex-col min-h-[100vh] min-h-[100dvh] bg-gradient-to-b from-blue-50 to-white">
      <Navbar initialAuth={initialAuth} />
      
      {showStepper && <StepperWithContext />}
      
      <main className="flex-1 flex flex-col justify-start px-2 sm:px-0 pt-4 pb-4 sm:pt-8 sm:pb-8 max-w-4xl w-full mx-auto z-10">
        {children}
      </main>
      <Footer />
    </div>
  );
}

export default function ClientSideLayout({ children, initialAuth }: ClientSideLayoutProps) {
  const pathname = usePathname();
  const isLandingPage = pathname === '/leave-review';

  // Landing page doesn't need context providers
  if (isLandingPage) {
    return (
      <ClientSideLayoutContentNoContext initialAuth={initialAuth}>
        {children}
      </ClientSideLayoutContentNoContext>
    );
  }

  // Use error boundaries and Suspense for all context-dependent pages
  return (
    <LeaveReviewErrorBoundary>
      <LeaveReviewSessionProvider>
        <LeaveReviewDataProvider>
          <LeaveReviewNavigationProvider>
            <React.Suspense fallback={<Loading />}>
              <LeaveReviewErrorBoundary>
                <ClientSideLayoutContent initialAuth={initialAuth}>
                  {children}
                </ClientSideLayoutContent>
              </LeaveReviewErrorBoundary>
            </React.Suspense>
          </LeaveReviewNavigationProvider>
        </LeaveReviewDataProvider>
      </LeaveReviewSessionProvider>
    </LeaveReviewErrorBoundary>
  );
} 